import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Divider} from "primereact/divider";
import {Dropdown, type DropdownProps} from "primereact/dropdown";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {Toast} from "primereact/toast";
import {type ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {type AvmSpecSegment, SEGMENT} from "@reva/shared/reva-spec";

import I18nErrorMessage from "../component/I18nErrorMessage";
import ImmodropErrorMessage from "../component/ImmodropErrorMessage";
import {
    clear,
    selectAddressError,
    selectAddressLoading,
    selectAddressQuery,
    selectImmodropLoading,
    selectImmodropQueryError,
    selectImportAddress,
    selectImportJson,
    selectImportSegment,
    selectJsonParsingErrorI18n,
    selectValid,
    setSegment,
} from "../redux/importSlice";
import {finalizeImport, queryImmodropAsync, queryImportAddressAsync, queryJsonInputAsync} from "../redux/importThunk";
import {useRevaDispatch} from "../redux/revaHooks";
import {handlePreventDefault} from "../util/events";
import AddressCard from "./address/AddressCard";

const supportedSegmentsSelections: {value: AvmSpecSegment; labelI18n: string}[] = [
    {value: SEGMENT.WHG_K, labelI18n: "importDialog.segment.WHG_K"},
    {value: SEGMENT.EZFH_K, labelI18n: "importDialog.segment.EZFH_K"},
    {value: SEGMENT.MFHWGH_K, labelI18n: "importDialog.segment.MFHWGH_K"},
    {value: SEGMENT.GRST_K, labelI18n: "importDialog.segment.GRST_K"},
];
const supportedSegmentKeys = supportedSegmentsSelections.map((selection) => selection.value);

interface ImportDetailsProps {
    /** address typed into the override field, or null while the extracted address is shown unchanged */
    addressInput: string | null;
    setAddressInput: (addressInput: string | null) => void;
    /** segment picked in the override dropdown, or null while the extracted segment is shown unchanged */
    segmentInput: AvmSpecSegment | null;
    setSegmentInput: (segmentInput: AvmSpecSegment | null) => void;
}

function ImportDetails({addressInput, setAddressInput, segmentInput, setSegmentInput}: ImportDetailsProps) {
    const {t} = useTranslation();

    const dispatch = useRevaDispatch();

    const immodropLoading = useSelector(selectImmodropLoading);

    const addressLoading = useSelector(selectAddressLoading);
    const addressQueryError = useSelector(selectAddressError);
    const addressQuery = useSelector(selectAddressQuery);

    const importJson = useSelector(selectImportJson);
    const importSegment = useSelector(selectImportSegment);
    const importAddress = useSelector(selectImportAddress);

    const loading = immodropLoading || addressLoading;
    const currentAddress = addressInput === null ? addressQuery : addressInput;
    const segmentIsValid = importSegment !== null && supportedSegmentKeys.includes(importSegment);

    let segmentSelection: {value: AvmSpecSegment | null; label: string}[] = supportedSegmentsSelections.map(
        (segmentSelection) => {
            return {
                value: segmentSelection.value,
                label: t(segmentSelection.labelI18n),
            };
        },
    );
    if (!segmentIsValid) {
        // Der Eintrag trägt den aktuellen, ungültigen Wert - auch null, wenn im Exposé kein Segment
        // gefunden wurde. Ohne ihn zeigte das Dropdown nichts an und sähe aus, als sei schon ein
        // gültiges Segment gewählt.
        segmentSelection = [
            ...segmentSelection,
            {
                value: importSegment,
                label:
                    importSegment === null
                        ? t("importDialog.segment.notDetected")
                        : t("importDialog.segment.notSupported", {segment: importSegment}),
            },
        ];
    }

    const currentSegment = segmentInput === null ? importSegment : segmentInput;

    const onUpdateAddress = () => {
        void dispatch(queryImportAddressAsync(currentAddress ?? ""));
    };

    const onUpdateSegment: NonNullable<DropdownProps["onChange"]> = (e) => {
        // Der Eintrag für ein nicht unterstütztes Segment trägt dessen Wert, und der kann null sein:
        // nicht jedes Exposé liefert ein Segment.
        const newSegment: AvmSpecSegment | null = e.value;
        setSegmentInput(newSegment);
        if (newSegment !== null && supportedSegmentKeys.includes(newSegment)) {
            dispatch(setSegment({segment: newSegment}));
        }
    };

    if (!importJson) {
        return null;
    }

    return (
        <>
            <Divider />

            <div className="flex w-full field items-center">
                <label htmlFor="segmentInput" className="mr-8 mb-0 font-bold w-32">
                    {t("importDialog.segment.label")}
                </label>

                <Dropdown
                    id="segmentInput"
                    className="flex-1"
                    value={currentSegment}
                    onChange={onUpdateSegment}
                    invalid={!segmentIsValid}
                    options={segmentSelection}
                />
            </div>

            <div className="flex w-full field items-center">
                <label htmlFor="addressInput" className="mr-8 mb-0 font-bold w-32">
                    {t("importDialog.address.label")}
                </label>
                <InputText
                    id="addressInput"
                    value={currentAddress}
                    onChange={(e) => setAddressInput(e.target.value)}
                    className="flex-1"
                    disabled={loading}
                    invalid={!!addressQueryError}
                />
                <Button
                    id="setAddressButton"
                    label={t("importDialog.address.submit")}
                    icon="pi pi-pencil"
                    onClick={onUpdateAddress}
                    className="ml-4"
                    disabled={loading}
                    loading={loading}
                    loadingIcon="pi pi-spin pi-spinner"
                />
            </div>

            <I18nErrorMessage errorObject={addressQueryError} />

            {!!importAddress && (
                <div className="flex w-full items-center">
                    <div className="mr-8 mb-0 w-32"></div>
                    <AddressCard addressMeta={importAddress} className="flex-1" />
                </div>
            )}
        </>
    );
}

/** Props both import dialogs take. */
interface ImportDialogProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    /** text the dialog's own input starts with whenever it opens, e.g. the address typed on the start page */
    initialText?: string;
}

interface ImportDialogStateOptions extends ImportDialogProps {
    /** setter of the input the dialog brings itself, e.g. the Immodrop URL or the JSON text */
    setSpecificInput: (text: string) => void;
}

/**
 * Shared state/behavior of both import dialogs: the address/segment override fields, resetting
 * them (plus the dialog-specific input, e.g. the Immodrop URL or the JSON text) whenever the
 * dialog opens, and Cancel/Submit. `onCancel` also backs the Dialog's `onHide` (X icon / ESC /
 * backdrop click) so every way of closing the dialog clears importSlice state the same way -
 * otherwise a query result/error from an abandoned dialog stays in the store (see TabObjectImmodrop).
 */
function useImportDialogState({visible, setVisible, initialText, setSpecificInput}: ImportDialogStateOptions) {
    const dispatch = useRevaDispatch();
    const importDataValid = useSelector(selectValid);

    const [addressInput, setAddressInput] = useState<string | null>(null);
    const [segmentInput, setSegmentInput] = useState<AvmSpecSegment | null>(null);

    const resetUiState = useCallback(() => {
        setSpecificInput(initialText ?? "");
        setAddressInput(null);
        setSegmentInput(null);
    }, [initialText, setSpecificInput]);

    useEffect(() => {
        if (visible) resetUiState();
    }, [visible, resetUiState]);

    const onCancel = () => {
        dispatch(clear());
        resetUiState();
        setVisible(false);
    };

    const onFinalizeImport = () => {
        void dispatch(finalizeImport());
        resetUiState();
        setVisible(false);
    };

    return {importDataValid, addressInput, setAddressInput, segmentInput, setSegmentInput, onCancel, onFinalizeImport};
}

interface ImportDialogFrameProps {
    visible: boolean;
    /** enables the submit button */
    importDataValid: boolean;
    onCancel: () => void;
    onFinalizeImport: () => void;
    children: ReactNode;
}

function ImportDialogFrame({visible, importDataValid, onCancel, onFinalizeImport, children}: ImportDialogFrameProps) {
    const {t} = useTranslation();

    return (
        <Dialog
            id="importObject"
            header={t("importDialog.title")}
            footer={
                <div>
                    <Button
                        label={t("importDialog.cancel")}
                        icon="pi pi-times"
                        severity="danger"
                        text
                        onClick={onCancel}
                    />
                    <Button
                        label={t("importDialog.submit")}
                        icon="pi pi-check"
                        onClick={onFinalizeImport}
                        disabled={!importDataValid}
                    />
                </div>
            }
            style={{minWidth: "70rem", minHeight: "52rem"}}
            visible={visible}
            onHide={onCancel}
            closable={false}
        >
            {children}
        </Dialog>
    );
}

export function DialogImmodropImport({visible, setVisible, initialText}: ImportDialogProps) {
    const {t} = useTranslation();

    const toastRef = useRef<Toast>(null);

    const [immodropInput, setImmodropInput] = useState(initialText ?? "");

    const {importDataValid, addressInput, setAddressInput, segmentInput, setSegmentInput, onCancel, onFinalizeImport} =
        useImportDialogState({visible, setVisible, initialText, setSpecificInput: setImmodropInput});

    const dispatch = useRevaDispatch();

    const immodropLoading = useSelector(selectImmodropLoading);
    const immodropQueryError = useSelector(selectImmodropQueryError);

    const addressLoading = useSelector(selectAddressLoading);

    const importJson = useSelector(selectImportJson);

    const loading = immodropLoading || addressLoading;

    const immodropSuccessMessage = importJson
        ? t("importDialog.immodrop.success", {
              size: Object.values(importJson).length,
          })
        : null;

    const onRunImmodrop = handlePreventDefault(() => {
        setAddressInput(null);
        setSegmentInput(null);
        void dispatch(queryImmodropAsync(immodropInput));
    });

    const onJsonCopy = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(importJson, null, 4));
            toastRef.current?.show({
                severity: "info",
                summary: "Info",
                detail: t("importDialog.immodrop.copiedJsonToClipboard"),
            });
        } catch (e) {
            console.warn("Could not copy JSON to clipboard", e);
        }
    };

    return (
        <ImportDialogFrame
            visible={visible}
            importDataValid={importDataValid}
            onCancel={onCancel}
            onFinalizeImport={onFinalizeImport}
        >
            <Toast ref={toastRef} />

            <form className="flex w-full field items-center" onSubmit={onRunImmodrop}>
                <label htmlFor="urlInput" className="mr-8 mb-0 font-bold w-32">
                    {t("importDialog.immodrop.url.label")}
                </label>
                <IconField iconPosition="left" className="flex-1">
                    <InputIcon className="pi pi-globe" />
                    <InputText
                        id="urlInput"
                        value={immodropInput}
                        onChange={(e) => setImmodropInput(e.target.value)}
                        placeholder={t("importDialog.immodrop.url.example")}
                        className="w-full"
                        disabled={loading}
                        invalid={!!immodropQueryError}
                    />
                </IconField>
                <Button
                    id="runImmodropButton"
                    label={t("importDialog.immodrop.url.submit")}
                    icon="pi pi-lightbulb"
                    onClick={onRunImmodrop}
                    disabled={loading}
                    className="ml-4"
                    loading={loading}
                    loadingIcon="pi pi-spin pi-spinner"
                />
            </form>
            <ImmodropErrorMessage error={immodropQueryError} />
            {!!immodropSuccessMessage && (
                <Message
                    severity="success"
                    className="w-full"
                    content={
                        <>
                            <span className="pi pi-check mr-2"></span>
                            <span>{immodropSuccessMessage}</span>
                            <Button
                                size="small"
                                icon="pi pi-question-circle"
                                className="ml-4"
                                text
                                tooltip={JSON.stringify(importJson, null, 4)}
                                tooltipOptions={{
                                    pt: {text: {style: {whiteSpace: "pre"}}},
                                    autoHide: false,
                                }}
                                onClick={() => void onJsonCopy()}
                            />
                        </>
                    }
                />
            )}

            <ImportDetails
                addressInput={addressInput}
                setAddressInput={setAddressInput}
                segmentInput={segmentInput}
                setSegmentInput={setSegmentInput}
            />
        </ImportDialogFrame>
    );
}

export function DialogJsonImport({visible, setVisible, initialText}: ImportDialogProps) {
    const {t} = useTranslation();

    const [jsonInput, setJsonInput] = useState(initialText ?? "");

    const {importDataValid, addressInput, setAddressInput, segmentInput, setSegmentInput, onCancel, onFinalizeImport} =
        useImportDialogState({visible, setVisible, initialText, setSpecificInput: setJsonInput});

    const dispatch = useRevaDispatch();

    const addressLoading = useSelector(selectAddressLoading);

    const jsonInputParsingErrorI18n = useSelector(selectJsonParsingErrorI18n);

    const importJson = useSelector(selectImportJson);

    const loading = addressLoading;

    const onRunJsonImport = handlePreventDefault(() => {
        setAddressInput(null);
        setSegmentInput(null);
        void dispatch(queryJsonInputAsync(jsonInput));
    });

    return (
        <ImportDialogFrame
            visible={visible}
            importDataValid={importDataValid}
            onCancel={onCancel}
            onFinalizeImport={onFinalizeImport}
        >
            <form className="flex w-full field items-start" onSubmit={onRunJsonImport}>
                <label htmlFor="jsonInput" className="mr-8 mb-0 font-bold w-32">
                    {t("importDialog.json.input")}
                </label>
                <InputTextarea
                    id="jsonInput"
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                    rows={importJson ? 6 : 20}
                    disabled={loading}
                    invalid={!!jsonInputParsingErrorI18n}
                    className="flex-1"
                    style={{resize: "vertical"}}
                />

                <Button
                    id="runJsonInputButton"
                    label={t("importDialog.json.parse")}
                    icon={PrimeIcons.PLAY}
                    onClick={onRunJsonImport}
                    disabled={loading}
                    className="ml-4"
                    loading={loading}
                    loadingIcon="pi pi-spin pi-spinner"
                />
            </form>
            <I18nErrorMessage i18nKey={jsonInputParsingErrorI18n} />

            <ImportDetails
                addressInput={addressInput}
                setAddressInput={setAddressInput}
                segmentInput={segmentInput}
                setSegmentInput={setSegmentInput}
            />
        </ImportDialogFrame>
    );
}
